import React, { useState, useEffect, useCallback } from 'react';

const rugImages = [
  'KathyMarty_ColumbiaRiver_300dpi_small.jpg',
  'KathyMarty_CoralReef_300dpi_small.jpg',
  'KathyMarty_landscape.jpg',
  'WHW_Nike.jpg',
  'WHWrug_widebluestripes_small.jpg',
  'WHW_maroon.jpg',
  '07.WHWRug060_1400x933.jpg',
  '17.WHWRug240_1400x933.jpg',
  'KathyMarty_SteamboatLanding_300dpi_small.jpg',
  '33.WHWRug58_1400x933.jpg',
  '35.WHWRug110_1400x933.jpg',
  'WHW_turq.jpg',
  'Kathy_Marty_striped.jpg',
  'KathyMarty_landscapeRug7_cropped2_300dpi_small.jpg',
  '21.WHWRug48_1400x933.jpg',
  '30.WHWRug264_1400x933.jpg',
  '11.WHWRug116__1400X933.jpg',
  '31.WHWRug265_1400x9336.jpg',
  'KMarty_blk-gray-wht-rug443_3.41MB_small.jpg',
  'KathyMarty_landscaperug3_small.jpg',
].map(f => `/images/gallery/rugs/${f}`);

const decorImages = [
  'K.Marty.pillows.jpg',
  'Pillows_24303983.jpg',
  'Pillows_24303989.jpg',
  'KathyMarty_pillows2_small.jpg',
  'Pillows_red.jpg',
  'KathyMarty_pillows_small1.jpg',
  'Pillows_24304166.jpg',
  'Pillows_pendleton_small.jpg',
  'Pillows_aqua.jpg',
  'wine.jpg',
  'KathyMarty_winetotes_small.jpg',
  'KathyMartywinetote_flowers3_small.jpg',
  'Marty.2026.plant.jpg',
  'K.Marty_plantbasket_small.jpg',
  'Winetote_coasters_plantbaskets_small.jpg',
  'Ornaments_small.jpg',
].map(f => `/images/gallery/decor/${f}`);

const jacketImages = [
  'KathyMarty_jacket-horse.jpg',
  'Jacket_24304268.jpg',
  'KMarty_upcycledjacket_small.jpg',
  'Jacket_24304353.jpg',
  'Jacket_24304485.jpg',
  'Jacket_24304277.jpg',
  'Jacket_24304280.jpg',
  'Jacket_24304274.jpg',
  'Jacket_24304361.jpg',
  'KathyMarty_upcycledjacket_small.jpg',
  'Marty.2026.jacket1_small.jpg',
  'Jacket_24304350.jpg',
  'KMarty_jacket_small.jpg',
  'Jacket_brown.jpg',
  'Kathy_Marty_jacket_small.jpg',
  'Jacket_Amer.Treasure_small.jpg',
].map(f => `/images/gallery/jackets/${f}`);

const categories = [
  {
    id: 'rugs',
    title: 'Handwoven Rugs',
    description: 'Whether your style is contemporary or traditional; bold or understated, Windy Hill Weavers\u2019 one-of-a-kind handwoven rugs will add warmth and color to any room in your home or office. I design 24- and 30-inch-wide area rugs of any length. I gladly welcome custom orders! Pricing begins at $95.',
    images: rugImages,
  },
  {
    id: 'decor',
    title: 'Home D\u00e9cor',
    description: 'Brighten your home with beautiful, hand crafted pillows, wine totes, flower totes, plant baskets, coasters, and ornaments, all made from quality Pendleton<sup>&copy;</sup> wool fabric. Contact me for availability, pricing, or custom orders.',
    images: decorImages,
  },
  {
    id: 'jackets',
    title: 'Upcycled Jackets',
    description: 'I turn denim jackets into stunning works of art by sewing custom-tailored Pendleton<sup>&copy;</sup> wool fabric to the back of the jacket. Choose a finished jacket, or customize your own with your choice of colors and patterns. Prices range from $59\u2013$69 for a child\u2019s jacket to $89\u2013$110 for an adult\u2019s jacket.',
    images: jacketImages,
  },
];

function Lightbox({ images, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, onPrev, onNext]);

  return (
    <div className="lightbox" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose} aria-label="Close">&times;</button>
      <button className="lightbox-prev" onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label="Previous">&lsaquo;</button>
      <img
        src={images[index]}
        alt={`Gallery ${index + 1}`}
        className="lightbox-img"
        onClick={(e) => e.stopPropagation()}
      />
      <button className="lightbox-next" onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label="Next">&rsaquo;</button>
      <div className="lightbox-counter">{index + 1} / {images.length}</div>
    </div>
  );
}

function Gallery() {
  const [lightbox, setLightbox] = useState(null);

  const openLightbox = (images, index) => setLightbox({ images, index });
  const closeLightbox = useCallback(() => setLightbox(null), []);
  const prevImage = useCallback(() => {
    setLightbox(prev => ({
      ...prev,
      index: prev.index <= 0 ? prev.images.length - 1 : prev.index - 1,
    }));
  }, []);
  const nextImage = useCallback(() => {
    setLightbox(prev => ({
      ...prev,
      index: prev.index >= prev.images.length - 1 ? 0 : prev.index + 1,
    }));
  }, []);

  useEffect(() => {
    if (lightbox) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [lightbox]);

  return (
    <section className="section gallery-section" id="gallery">
      {categories.map((cat) => (
        <div className="gallery-category" key={cat.id} id={`gallery-${cat.id}`}>
          <div className="gallery-category-header">
            <h3>{cat.title}</h3>
            <div className="section-divider" />
            <p dangerouslySetInnerHTML={{ __html: cat.description }} />
          </div>
          <div className="gallery-grid">
            {cat.images.map((src, i) => (
              <div
                className="gallery-item"
                key={i}
                onClick={() => openLightbox(cat.images, i)}
              >
                <img src={src} alt={`${cat.title} ${i + 1}`} loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      ))}

      {lightbox && (
        <Lightbox
          images={lightbox.images}
          index={lightbox.index}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </section>
  );
}

export default Gallery;
